import React, { useState, useEffect, useContext } from 'react'
import ETRFileUploadComponent from '../record-transfer/etr-issuance/etr-attachments'
import { ReactComponent as IconChevronDown } from '@/assets/svgs/chevron-down.svg'
import { ReactComponent as IconWarning } from '@/assets/svgs/DesignSystem/DS_Icon_info.svg'
import { ReactComponent as IconNoMessage } from '@/assets/svgs/no-message.svg'
import { ReactComponent as IconProfileChevronDown } from '@/assets/svgs/chevron-down-small.svg';
import Modal from '@/componentsDS/Modal/Modal'
import CardConfigure from '@/componentsDS/Modal/CardConfigure'
import FloatingPanel from '@/componentsDS/FloatingPanel/FloatingPanel.jsx'
import { FloatingPanelType } from '@/componentsDS/FloatingPanel/FloatingPanel.type.js'
import { useNavigate } from '@/lib/router-dom'
import { ToastContext } from '@/context/toast/index'
import Callout from '@/componentsDS/Callout/Callout'
import { CalloutType, CalloutVariant } from '@/componentsDS/Callout/Callout.type'
import Button from '@/components/Button/index'
import { useLocation } from '@/lib/router-dom/index'
import useMasterDataManagement from '@/hooks/use-master-data-management/index'
import usePage from '@/hooks/use-page/index'
import { useBuildex } from '@/hooks/use-buildex/index'
import { useFeature } from '@/hooks/use-feature/index'
import { featureConstant } from '@/constants/configuration/feature'
import Breadcrumbs from "@/componentsDS/Breadcrumbs/index.jsx";
import MultiLevelDropDown from '@/components/MultiLevelMenuDropDown/index'
import api from '@/services/api/master-data-management.js'

const MasterDataManagementUploadPage = () => {
	const isBuildex = useBuildex();
	const isSupersetDashboard = useFeature(featureConstant.DASHBOARD);
	const isMasterDataUploadEnabled = useFeature(featureConstant.MASTER_DATA_UPLOAD);
	const isMasterDataUplaodHDBEnabled = useFeature(featureConstant.MASTER_DATA_UPLOAD_HDB);
	const [fileLists, setFileLists] = useState([])
	const [validationErrors, setValidationErrors] = useState([])
	const [list, setList] = useState(null);
	const [currentPage, setCurrentPage] = useState(1)
	const [itemsPerPage, setItemsPerPage] = useState(10)
	const isHomePage = !(isBuildex && isSupersetDashboard);
	const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' })
	const [showConfirmModal, setShowConfirmModal] = useState(false)
	const [loading, setLoading] = useState(false)
	const [downloadLoading, setDownloadLoading] = useState(false)
	const { currentState } = useLocation();
	const [pageTitle] = useState("Master Data Management");
	const { goTo } = useNavigate()
	const { addToastMessage } = useContext(ToastContext)
	
	const { breadcrumbsItems } = usePage({
		title: pageTitle,
		breadcrumbs: [
			{
				label: "",
				link: `/`,
				isHomePage: isHomePage,
			},
			{
				label: "Master Data Management",
				link: `/master-data-management`,
			},
			{
				label: `${currentState?.dataElementName}`,
				link: ``,
			},
		],
	});
	useEffect(() => {
		if (!currentState?.schema || !currentState?.dataElementName || !currentState?.entityId) {
			addToastMessage('Please choose a master data to update.', 'error');
			goTo('/master-data-management');
		}
	}, [])

	const { updateMasterData } = useMasterDataManagement();

	const sortedData = list ? [...list].sort((a, b) => {
		if (!sortConfig.key) return 0;

		let aValue = a[sortConfig.key];
		let bValue = b[sortConfig.key];

		if (aValue === null || aValue === undefined) return 1;
		if (bValue === null || bValue === undefined) return -1;

		aValue = String(aValue).trim();
		bValue = String(bValue).trim();

		const aDate = new Date(aValue);
		const bDate = new Date(bValue);

		if (!Number.isNaN(aDate.getTime()) && !Number.isNaN(bDate.getTime())) {
			return sortConfig.direction === 'asc'
				? aDate - bDate
				: bDate - aDate;
		}

		const aNum = Number.parseFloat(aValue);
		const bNum = Number.parseFloat(bValue);

		if (!Number.isNaN(aNum) && !Number.isNaN(bNum)) {
			return sortConfig.direction === 'asc'
				? aNum - bNum
				: bNum - aNum;
		}

		return sortConfig.direction === 'asc'
			? aValue.localeCompare(bValue)
			: bValue.localeCompare(aValue);
	}) : [];

	const totalPages = sortedData ? Math.ceil(sortedData.length / itemsPerPage) : 0
	const startIndex = (currentPage - 1) * itemsPerPage
	const endIndex = startIndex + itemsPerPage
	const currentPageData = sortedData ? sortedData.slice(startIndex, endIndex) : []

	const headers = [];
	const entitySchema = currentState?.schema?.items?.properties ?? {}
	const columnRef = list && list.length > 0 ? Object.keys(entitySchema) : [];
	Object.keys(entitySchema).forEach((key) => {
		if (entitySchema[key].title) {
			headers.push({ key, title: entitySchema[key].title });
		}
		else {
			headers.push({ key, title: formatHeader(key) });
		}
	});

	function formatHeader(key) {
		return key
			.replace(/([A-Z])/g, " $1")
			.replace(/_/g, " ")
			.replace(/\b\w/g, (c) => c.toUpperCase());
	}

	function formatValue(value) {
		if (!value) return "-";

		if (isMasterDataUplaodHDBEnabled){
			if (Array.isArray(value) || typeof value === 'object') {
				return value.length; 
			}
		}
		
		return value;
	}

	const getClassName = (name) => {
		if (!sortConfig.key || sortConfig.key !== name) {
			return '';
		}
		return sortConfig.direction === 'asc' ? 'asc' : 'desc';
	}

	useEffect(() => {
		setCurrentPage(1)
	}, [list])

	const goToPrev = () => {
		if (currentPage === 1) return
		setCurrentPage(prev => prev - 1)
	}

	const goToNext = () => {
		if (currentPage === totalPages) return
		setCurrentPage(prev => prev + 1)
	}

	const handleSort = (key) => {
		let direction = 'asc';
		if (sortConfig.key === key && sortConfig.direction === 'asc') {
			direction = 'desc';
		}
		setSortConfig({ key, direction });
		setCurrentPage(1);
	}

	const goBack = () => {
		goTo('/master-data-management')
	}

	const handleValidationResult = (result) => {
		if (result.parsedData) {
			setList(result.parsedData);
		} else if (result.parsedData === null) {
			setList(null);
		}

		if (result.isValid) {
			setValidationErrors([]);
		} else {
			const formattedErrors = result.errors.map((error, index) => ({
				type: 'ERROR',
				message: `${error.field ? `${error.field}, ` : ''}Row ${error.row}: ${error.message}`,
				ref: null
			}));
			setValidationErrors(formattedErrors);
		}
	};

	const handleCancel = () => {
		setValidationErrors([]);
		setList(null);
		goBack()
	}

	const handleSubmit = () => {
		if (list && list.length > 0) {
			setShowConfirmModal(true)
		}
	}

	const handleConfirmSubmit = async () => {
		setLoading(true)

		const [error, data] = await updateMasterData({ data: list, entityId: currentState?.entityId })

		if (error) {
			setShowConfirmModal(false)
			addToastMessage('Failed to update master data. Please try again.', 'error')
			setLoading(false)
			return
		}

		if (data) {
			setShowConfirmModal(false)
			goTo('/master-data-management')
			addToastMessage(
				'Master data updated successfully.',
				'success',
				<Button
					text="View Updates"
					ghost
					round
					noborder
					nohover
					submit={() => goTo(`/master-data-management/details/${currentState?.entityId}`, { item: { name: currentState?.dataElementName, category: currentState?.category, jsonSchema: currentState?.schema, id: currentState?.entityId, modified: data?.modifiedAt, modifiedBy: data?.modifiedBy } })}
					style={{ fontSize: '14px', padding: '4px 8px' }}
				/>
			)
		}
		setLoading(false)
	}

	const handleCancelSubmit = () => {
		setShowConfirmModal(false)
	}

	/*=================================================
		Download CSV Template and Master Data
	/================================================*/
	function menuItemsClickFunction(e, type, id) {
		e.stopPropagation();
		if (type === "download") {
			downloadCSVTemplateMasterData(id, "download");
		} else if (type === "get-template") {
			downloadCSVTemplateMasterData(id, "get-template");
		}
	}

	async function downloadCSVTemplateMasterData(id, type) {
		setDownloadLoading(true);
		const [error, result] = await api.downloadCSVTemplateMasterData(id, type);
		setDownloadLoading(false);
		if (error) {
			return addToastMessage(
				error?.error?.message || "Failed to download file(s)",
				'error'
			);
		}
		if (!result?.response) {
			return addToastMessage("Received empty response from server", 'error');
		}
		const link = document.createElement("a");
		const href = `data:text/csv;charset=utf-8,%EF%BB%BF${encodeURIComponent(
			result.response
		)}`;
		link.href = href;
		link.download = `${result?.filename ?? type + ".csv"}`;
		link.click();
	}

	return (
		<>
			<Breadcrumbs breadcrumbItems={breadcrumbsItems} />
			<div className="title-header">
				<div className="title-header__left">
					<div className="title-header__title">
						Update Master Data
					</div>
				</div>
				<div className="title-header__right">
					<div className="title-header__right__update-data-text">
						<MultiLevelDropDown
							button={() => (
								<div className="title-header__right__update-data-text__button">
									<span>Download</span>
									<IconProfileChevronDown />
								</div>
							)}
							menuList={[
								{
									label: "Download Master Data",
									onClick: (e) => menuItemsClickFunction(e, "download", currentState?.entityId),
									disabled: downloadLoading
								},
								{
									label: "Download CSV Template",
									onClick: (e) => menuItemsClickFunction(e, "get-template", currentState?.entityId),
									disabled: downloadLoading
								},
							]}
							disabled={downloadLoading}
						/>
					</div>
				</div>
			</div>

			<div className="layout-default__right__bottom">
				<div className="data-table__content">
					<div className="data-table__content__cols">
						<div className="data-table__content__col">
							<div className="data-table__table">
								<div className='data-table__table__head__row__left' style={{ fontWeight: 800, height: "4rem", paddingTop: "1.6rem", marginBottom: '1rem', borderBottom: '1px solid #e0e0e0' }}>
									{currentState?.dataElementName}
								</div>

								{/* File Upload Section */}
								<div style={{ padding: '1rem 1.5rem' }}>
									<ETRFileUploadComponent
										fileLists={fileLists}
										setFileLists={setFileLists}
										isReadOnly={false}
										maxFileSize={31457280} /** 30MB */
										maxFileSizeLabel="30MB"
										uploadMessage="Upload your CSV file here. You may upload only 1 attachment."
										multiple={false}
										accept={".csv"}
										acceptedFileTypes={['.csv']}
										validateOnUpload={true}
										validationSchema={currentState?.schema}
										onValidationResult={handleValidationResult}
										uniqueKeys={currentState?.uniqueKeysOfSchema}
										showApiSpecUrl={!isMasterDataUploadEnabled}
									/>
								</div>
							</div>
						</div>
					</div>
				</div>

				{/* Info Callout Component */}
				{(!fileLists || fileLists.length === 0) && <Callout
					data-testid="master-data-management-upload-callout"
					className="DS__callout--master-data-management-upload-callout"
					type={CalloutType.INFO}
					variant={CalloutVariant.STANDARD}
					message="You will override the existing master data by submitting. This action will be irreversible."
				/>}

				{/* Divider line before table */}
				{fileLists?.length > 0 && validationErrors.length === 0 && (
					<div style={{ borderTop: '1px solid #e5e7eb', margin: '2rem 0 2rem 0' }}></div>
				)}

				{/* Table section - only render if data exists and no validation errors */}
				{fileLists?.length > 0 && validationErrors.length === 0 && (
					<div className="etr-issuance-record-data">
						<div>
							<div className="data-table__table__body">
								<div>
									{/* File name header */}
									<div className="master-data-management-details__file-name">{fileLists[0]?.name || 'CSV DATA'}</div>
									<div className="data-table__table__head__row" style={{ border: 'none' }}>
										<div className="data-table__table__body__row-cursor data-table__table__body__content-master-data_cell master-data-table__header-row">
											{headers?.map((header, idx) => (
												<div key={idx} className="tooltip">
													<div
														key={header.key}
														className={`data-table__table__head__title data-table__table__head__title__sort-order etr-issuance-data-table__table__head__title-master-data-name link ${getClassName(
															header.key
														)} master-data-table__header-row-cell`}
														onClick={() => handleSort(header.key)}
														onKeyDown={() => handleSort(header.key)}
														id={header.key}
														role="none"
														style={{ paddingLeft: '1.5rem' }}
													>
														<div className="master-data-table__text-ellipsis master-data-table__text-ellipsis--header">
															{header.title}
														</div>
														<div className="etr-maker-checker-btn-group">
															<button className="data-table__table__head__icon__sort-order-up">
																<IconChevronDown />
															</button>
															<button className="data-table__table__head__icon__sort-order-down">
																<IconChevronDown />
															</button>
														</div>
													</div>
													<div className="tooltiptext eform-tooltip-text">
														{header.title}
													</div>
												</div>
											))}
										</div>
									</div>
									{currentPageData?.length > 0 ? (
										currentPageData.map((item, rowIndex) => (
											<div className="data-table__table__body__row master-data-table__body-row" key={rowIndex}>
												<div className="data-table__table__body__row-cursor">
													{columnRef.map((header) => (
														<div
															key={header}
															className="data-table__table__body__content etr-issuance-data-table__table__body__content-master-data-name data-table__table__body__content-master-data_cell master-data-table__body-row-cell"
															data-testid={header}
														>
															<div className="master-data-table__text-ellipsis">
																{formatValue(item?.[header])}
															</div>
														</div>
													))}
												</div>
											</div>
										))
									) : (
										<div className="etr-issuance-no-message-record">
											<div className="data-table__empty__icon">
												<IconNoMessage />
												<div>No data available</div>
											</div>
										</div>
									)}
								</div>
							</div>
						</div>
						{list?.length > 0 && (
							<div className="data-table__table__body-footer">
								<div className="data-table__table__footer">
									<div className="data-table__table__footer__row">
										<div className="data-table__table__footer__row__left">
											<div className="data-table__table__meta">
												Showing {startIndex + 1} to {Math.min(endIndex, sortedData.length)} of {sortedData.length} entries
											</div>
										</div>
										<div className="data-table__table__footer__row__right">
											<div className="data-table__table__pagination">
												<input
													className={
														"etr-maker-checker__data-table__table__pagination__page"
													}
													type="number"
													value={currentPage}
													onChange={(e) => {
														setCurrentPage(
															Number(e.target.value) || 1
														);
													}}
													min="1"
												/>
												<span className="data-table__table__pagination__page__meta">
													{" "}
													of {totalPages || 1} pages{" "}
												</span>
												<button
													data-testid="pagination-prev"
													className={
														"etr-maker-checker__data-table__table__pagination__prev"
													}
													onClick={() => goToPrev()}
												>
													<IconChevronDown />
												</button>
												<button
													data-testid="pagination-next"
													className={
														"etr-maker-checker__data-table__table__pagination__next"
													}
													onClick={() => goToNext()}
												>
													<IconChevronDown />
												</button>
											</div>
										</div>
									</div>
								</div>
							</div>
						)}
					</div>
				)}

				{/* Sticky Actions Footer */}
				<div className="DS__layout__footer">
					<div className="layout-default__sticky-actions">
						<div className="layout-default__sticky-actions__left">
							<Button
								data-testid="master-data-management-upload-cancel-btn"
								ghost
								round
								noborder
								text="Cancel"
								submit={handleCancel}
							/>
						</div>

						<div className="layout-default__sticky-actions__right" >
							<div className="overview__button_wrap">
								<Button
									data-testid="master-data-management-upload-submit-btn"
									round
									text="Submit"
									submit={handleSubmit}
									disabled={!fileLists || fileLists.length === 0 || currentPageData.length === 0}
								/>

							</div>
						</div>
					</div>
				</div>
			</div>

			{/* CSV Validation Errors using FloatingPanel */}
			{validationErrors.length > 0 && (
				<FloatingPanel
					title={`${validationErrors.length} error${validationErrors.length > 1 ? 's' : ''} found`}
					messageHeader="Please correct the errors below and upload the file again:"
					messages={validationErrors.map(error => ({ message: error.message }))}
					type={FloatingPanelType.DESTRUCTIVE}
					className='master-data-floating-panel'
				/>
			)}

			{/* Confirmation Modal */}
			<Modal visible={showConfirmModal} variant='medium' close={handleCancelSubmit} dismissClickOutsideModal={false}>
				<CardConfigure>
					<CardConfigure.Header>
						<CardConfigure.Header.Title>
							<div className="card-configure__header__left">
								<div className="card-configure__title">
									<div className="card-configure__title__left card-configure__title__left--background-error">
										<IconWarning />
									</div>
									<div className="card-configure__title__right">
										<div className="card-configure__title__right__top">
											Replace Current Master Data
										</div>
									</div>
								</div>
							</div>
						</CardConfigure.Header.Title>
					</CardConfigure.Header>
					<CardConfigure.Body>
						<CardConfigure.Body.Col>
							<div style={{ fontSize: '16px', textAlign: 'center', lineHeight: '1.5' }}>
								You will be replacing the existing master data. This action is irreversible. Would you like to continue?
							</div>
						</CardConfigure.Body.Col>
					</CardConfigure.Body>
					<CardConfigure.Footer>
						<CardConfigure.Footer.Left>
							<Button
								text="Cancel"
								ghost
								round
								noborder
								textWarning
								warningbackgroundhover
								submit={handleCancelSubmit}
								disabled={loading}
							/>
						</CardConfigure.Footer.Left>
						<CardConfigure.Footer.Right>
							<Button
								text={"Continue"}
								round
								error
								warningbackgroundhover
								submit={handleConfirmSubmit}
								disabled={loading}
							/>
						</CardConfigure.Footer.Right>
					</CardConfigure.Footer>
				</CardConfigure>
			</Modal >

		</>
	)
}

export default MasterDataManagementUploadPage